import { type ValueOf } from '@dni-types/utilities/value-of'
import { type ECommerceOrdersRequest } from '@hooks-api/types/apiRequest'
import { useCallback, useMemo, useState } from 'react'

export const SEARCH_FIELD = {
  ORDER_ID: 'order_id',
  BILLING_FIRST_NAME: 'billing_first_name',
} as const

export type SearchFieldType = ValueOf<typeof SEARCH_FIELD>

export const SEARCH_FIELD_OPTIONS = [
  { label: '訂單編號', value: SEARCH_FIELD.ORDER_ID },
  { label: '收件人資訊', value: SEARCH_FIELD.BILLING_FIRST_NAME },
] as const

interface AppliedSearchType {
  field: SearchFieldType
  value: string
}

const DEFAULT_APPLIED_SEARCH: AppliedSearchType = {
  field: SEARCH_FIELD.ORDER_ID,
  value: '',
}

type OrderSearchParams = Partial<Pick<ECommerceOrdersRequest, 'order_id' | 'billing_first_name'>>

const EMPTY_SEARCH_PARAMS: OrderSearchParams = {}

export const useOrderSearch = () => {
  const [appliedSearch, setAppliedSearch] = useState<AppliedSearchType>(DEFAULT_APPLIED_SEARCH)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [searchField, setSearchField] = useState<SearchFieldType>(SEARCH_FIELD.ORDER_ID)

  const searchParams = useMemo((): OrderSearchParams => {
    if (!appliedSearch.value) return EMPTY_SEARCH_PARAMS
    return { [appliedSearch.field]: appliedSearch.value }
  }, [appliedSearch])

  const submitSearch = useCallback(() => {
    setAppliedSearch({ field: searchField, value: searchKeyword.trim() })
  }, [searchField, searchKeyword])

  const resetSearch = useCallback(() => {
    setAppliedSearch(DEFAULT_APPLIED_SEARCH)
    setSearchKeyword('')
    setSearchField(SEARCH_FIELD.ORDER_ID)
  }, [])

  const clearKeyword = useCallback(() => {
    setSearchKeyword('')
    setAppliedSearch(current => ({ ...current, value: '' }))
  }, [])

  const changeField = useCallback((field: SearchFieldType) => {
    setSearchField(field)
    setSearchKeyword('')
    setAppliedSearch(DEFAULT_APPLIED_SEARCH)
  }, [])

  return {
    searchParams,
    searchKeyword,
    searchField,
    setSearchKeyword,
    changeField,
    submitSearch,
    resetSearch,
    clearKeyword,
  }
}
